import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { TablePagination } from "@/shared/components/TablePagination";
import { queryClient } from "@/shared/services/queryClient";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useNotification } from "@/shared/hooks/useNotification";
import { collectionsApi, type CollectionRelease } from "../services/collectionsApi";
import { randomId } from "@/shared/utils/randomId";
import { useAuthStore } from "@/features/auth/stores/authStore";

const errorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

export function CustomerCollectionsPanel() {
  const notify = useNotification();
  const { hasPermission } = useAuthStore();
  const canConfirm = hasPermission("dispatch.collections.confirm");
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 300);
  const [page, setPage] = useState(1);
  const [target, setTarget] = useState<CollectionRelease | null>(null);
  const [collectorName, setCollectorName] = useState("");
  const [collectorPhone, setCollectorPhone] = useState("");
  const [collectorReference, setCollectorReference] = useState("");
  const [isCustomerPersonally, setIsCustomerPersonally] = useState(false);
  const [notes, setNotes] = useState("");
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const confirmationKey = useRef(`collection:${randomId()}`);
  const query = useQuery({
    queryKey: ["customer-collections", debounced, page],
    queryFn: () => collectionsApi.getReady(debounced, page),
    staleTime: 0,
    refetchOnMount: "always",
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["customer-collections"] });
  const confirm = useMutation({
    mutationFn: () => collectionsApi.confirm(target!.id, {
      collectorName: collectorName.trim(),
      isCustomerPersonally,
      collectorPhone: collectorPhone.trim() || undefined,
      collectorReference: collectorReference.trim() || undefined,
      notes: notes.trim() || undefined,
      rowVersion: target!.rowVersion,
      items: target!.items.filter(line => Number(quantities[line.id] ?? 0) > 0).map(line => ({
        fulfilmentReleaseItemId: line.id,
        collectedQuantity: Number(quantities[line.id]),
      })),
    }, confirmationKey.current),
    onSuccess: () => { notify.success("Customer collection confirmed"); setTarget(null); confirmationKey.current = `collection:${randomId()}`; refresh(); },
    onError: (error) => notify.error(errorMessage(error, "Could not confirm collection; retry with the same details")),
  });
  const total = useMemo(() => target?.items.reduce((sum, line) => sum + Number(quantities[line.id] ?? 0), 0) ?? 0, [target, quantities]);

  const openConfirmation = (release: CollectionRelease) => {
    confirmationKey.current = `collection:${randomId()}`;
    setTarget(release);
    setCollectorName(""); setCollectorPhone(""); setCollectorReference(""); setNotes(""); setIsCustomerPersonally(false);
    setQuantities(Object.fromEntries(release.items.map(line => [line.id, String(line.availableQuantity)])));
  };

  return <Card className="overflow-hidden">
    <CardHeader>
      <CardTitle>Customer Collection</CardTitle>
      <CardDescription>
        Growth selects and hands over the exact greenhouse stock. Confirm collection only after Dispatch has acknowledged receipt.
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search order or customer…" className="max-w-sm" />
      {query.isError && <div className="rounded-md border border-destructive/40 p-4 text-sm"><p>Could not load customer collections.</p><Button className="mt-2" variant="outline" size="sm" onClick={() => query.refetch()}>Retry</Button></div>}
      <Table className="table-fixed"><TableHeader><TableRow>
        <TableHead className="w-36">Order</TableHead><TableHead className="w-44">Customer</TableHead><TableHead className="w-24">Payment</TableHead><TableHead>Items</TableHead><TableHead className="w-36">Status</TableHead><TableHead className="w-52" />
      </TableRow></TableHeader><TableBody>
        {query.isLoading && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Loading customer collections…</TableCell></TableRow>}
        {!query.isLoading && !query.isError && (query.data?.items.length ?? 0) === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No collections are ready.</TableCell></TableRow>}
        {(query.data?.items ?? []).map(release => <TableRow key={release.id}>
          <TableCell className="align-top whitespace-normal break-words font-medium">{release.orderNumber}</TableCell>
          <TableCell className="align-top whitespace-normal break-words">{release.customerName}</TableCell>
          <TableCell className="align-top whitespace-normal">{release.paymentStatus}</TableCell>
          <TableCell className="space-y-2 align-top whitespace-normal break-words">{release.items.map(line => <div key={line.id} className="text-sm">
            <div className="font-medium">{line.productName}</div>
            <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              <span>{line.releasedQuantity} released</span>
              <span>{line.preparedQuantity} prepared</span>
              {line.collectedQuantity > 0 && <span>{line.collectedQuantity} collected</span>}
              <span>{line.availableQuantity} remaining</span>
            </div>
            {line.stockIssue && <span className="block text-xs text-destructive">{line.stockIssue}</span>}
          </div>)}</TableCell>
          <TableCell className="align-top whitespace-normal break-words">{release.collectionStatus}</TableCell>
          <TableCell className="align-top"><div className="flex flex-wrap justify-end gap-2">
            {canConfirm && <Button size="sm" disabled={release.handoverStatus !== "ReadyForRelease"} onClick={() => openConfirmation(release)}>Confirm Collection</Button>}
          </div>
          </TableCell>
        </TableRow>)}
      </TableBody></Table>
      {query.data && <TablePagination pageNumber={page} pageSize={25} totalCount={query.data.totalCount} totalPages={query.data.totalPages} onPageChange={setPage} onPageSizeChange={() => {}} isFetching={query.isFetching} />}
    </CardContent>
    <Dialog open={!!target} onOpenChange={open => !open && setTarget(null)}><DialogContent>
      <DialogHeader><DialogTitle>Confirm partial or full collection</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Collector name</Label><Input value={collectorName} onChange={e => setCollectorName(e.target.value)} /></div>
        <div className="flex items-center gap-2">
          <Checkbox id="customer-personally" checked={isCustomerPersonally} onCheckedChange={checked => {
            const personally = checked === true;
            setIsCustomerPersonally(personally);
            if (personally && target) setCollectorName(target.customerName);
          }} />
          <Label htmlFor="customer-personally">Collected personally by the customer</Label>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0"><div><Label>Phone (optional)</Label><Input value={collectorPhone} onChange={e => setCollectorPhone(e.target.value)} /></div><div><Label>Reference (optional)</Label><Input value={collectorReference} onChange={e => setCollectorReference(e.target.value)} /></div></div>
        {target?.items.map(line => <div key={line.id} className="grid grid-cols-[1fr_8rem] items-end gap-3"><div className="text-sm">{line.productName}<div className="text-muted-foreground">Remaining {line.availableQuantity}</div></div><div><Label>Collected</Label><Input type="number" min={0} max={line.availableQuantity} value={quantities[line.id] ?? ""} onChange={e => setQuantities(q => ({ ...q, [line.id]: e.target.value }))} /></div></div>)}
        <div><Label>Notes</Label><Textarea value={notes} onChange={e => setNotes(e.target.value)} /></div>
        <p className="text-sm font-medium">Total collected: {total}</p>
        {confirm.isError && <p className="text-sm text-destructive">{errorMessage(confirm.error, "Confirmation failed. Check the details and retry; the same retry key will be reused.")}</p>}
      </div>
      <DialogFooter><Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button><Button disabled={!collectorName.trim() || total <= 0 || confirm.isPending} onClick={() => confirm.mutate()}>{confirm.isPending ? "Confirming…" : confirm.isError ? "Retry confirmation" : "Confirm handover"}</Button></DialogFooter>
    </DialogContent></Dialog>
  </Card>;
}
